import React from "react";
import { Tip } from "@/components/fun-ui/Tip";
import { PhotoProvider, PhotoView } from '@/lib/third-party/react-photo-view/dist/react-photo-view';
import { IconButton } from "@/components/icon-button";
import { ChevronRightIcon, AspectRatioIcon, DownloadIcon, ReverseIcon, XIcon, ZoomInIcon, ZoomOutIcon } from "@/components/fun-ui/icons";
import { Separator } from "@/components/ui/separator";
import { ExtractImage } from "@/stores/slices/paperSlice";
import { formatTime } from "@/lib/utils";
import { MarkdownRenderer } from "../PaperDetail/MarkdownRenderer";
import { downloadBlobFile } from "@/lib/download";

export const ExtractChatView = ({
  images = []
}: {
  images?: ExtractImage[]
}) => {
  if (!images.length) {
    return <div
      className="absolute inset-0 top-5 bottom-5 px-5 text-annotation-foreground">文献未包含图表内容</div>
  }
  return (
    <PhotoProvider
      maskOpacity={0.8}
      bannerVisible={false}
      overlayRender={(params: any) => {
        const { images, index, onClose, onScale, scale, onRotate, rotate, overlay, onIndexChange } = params;
        return (
          <>
            <div
              className="absolute bottom-8 left-0 right-0 text-center text-background z-20 pointer-events-auto flex flex-col">
              <div className="flex justify-center w-full">
                <div className="flex flex-col bg-black/60 rounded-[8px] w-fit backdrop-blur max-w-[520px]">
                  {
                    !!overlay && <div className="text-start text-sm p-4 pb-0">
                      <div className="max-h-[120px] overflow-auto">{overlay}</div>
                    </div>
                  }
                  <div className="flex gap-6 items-center justify-center p-4">
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => onIndexChange(index - 1)}
                      label="上一张"
                    >
                      <ChevronRightIcon size={24} className="rotate-180" />
                    </IconButton>
                    <span className="-mx-6 w-16">{index + 1}/{images.length}</span>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => onIndexChange(index + 1)}
                      label="下一张"
                    >
                      <ChevronRightIcon size={24} />
                    </IconButton>
                    <div className="py-1 h-full w-fit">
                      <Separator className="bg-background/30" orientation="vertical" />
                    </div>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => {
                        onScale(scale - 0.2);
                      }}
                      label="缩小"
                    >
                      <ZoomOutIcon size={24} />
                    </IconButton>
                    <span className="-mx-2 w-12">{(scale * 100).toFixed(0)}%</span>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => {
                        onScale(scale + 0.2);
                      }}
                      label="放大"
                    >
                      <ZoomInIcon size={24} />
                    </IconButton>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => {
                        onScale(1);
                      }}
                      label="重置缩放"
                    >
                      <AspectRatioIcon size={24} />
                    </IconButton>
                    <div className="py-1 h-full w-fit">
                      <Separator className="bg-background/30" orientation="vertical" />
                    </div>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => {
                        onRotate(rotate - 90)
                      }}
                      label="向左旋转 90°"
                    >
                      <ReverseIcon size={24} />
                    </IconButton>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={async () => {
                        const src = images?.[index]?.src || ''
                        try {
                          if (src) {
                            const response = await fetch(src);
                            const blob = await response.blob();
                            // 根据MIME类型确定文件扩展名
                            let ext = 'png';
                            if (blob.type.includes('jpeg') || blob.type.includes('jpg')) {
                              ext = 'jpg';
                            } else if (blob.type.includes('png')) {
                              ext = 'png';
                            } else if (blob.type.includes('webp')) {
                              ext = 'webp';
                            }
                            // 使用统一的downloadBlobFile函数，确保Safari兼容性
                            downloadBlobFile(blob, `Findin 图表提取_${formatTime(Date.now(), 'YYYYMMDDHHmmss')}.${ext}`);
                          }
                        } catch (error) {
                          console.error('下载失败:', error);
                          alert('图片下载失败，请重试');
                        }
                      }}
                      label="下载图片"
                    >
                      <DownloadIcon size={24} />
                    </IconButton>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="absolute right-5 top-5 w-[60px] h-[60px] rounded-full bg-black/60 flex items-center justify-center text-background cursor-pointer hover:bg-black/30 z-20 pointer-events-auto backdrop-blur"
              onClick={() => {
                onClose();
              }}
            >
              <XIcon size={28} />
            </div>
          </>
        );
      }}
    >
      <div className="absolute inset-x-0 top-5 bottom-5 px-5 overflow-y-auto overflow-x-hidden no-scrollbar hover:custom-scrollbar">
        <div className="grid grid-cols-2 gap-x-4 gap-y-5 py-4">
          {
            images.map(({ caption, imagePath }) => {
              const captionMarkdown = <MarkdownRenderer>{caption}</MarkdownRenderer>;
              return (
                <PhotoView
                  key={imagePath}
                  src={imagePath}
                  overlay={captionMarkdown}
                >
                  <div className="flex flex-col flex-1">
                    <div
                      className="group flex border rounded justify-center hover:border-primary relative overflow-hidden"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={`${imagePath}?x-oss-process=image/resize,w_300/quality,q_90`} alt={caption} className="h-20" />
                    </div>
                    <Tip label={captionMarkdown} side="bottom" contentClassName="max-w-80">
                      <div
                        className="text-[10px] text-annotation-foreground text-center break-normal overflow-hidden w-full">
                        {captionMarkdown}
                      </div>
                    </Tip>
                  </div>
                </PhotoView>
              )
            })
          }
        </div>
      </div>
    </PhotoProvider>
  )
}